import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from "recharts";
import { cn } from "../../ui/utils";
import {
  CHART_PALETTE_DARK,
  CHART_PALETTE_LIGHT,
  OTHER_COLOR_DARK,
  OTHER_COLOR_LIGHT,
  BAR_FILL,
  MARK_STROKE_WIDTH,
  SEGMENT_PADDING_ANGLE,
  markGlow,
  formatCompact,
  formatNumber,
  formatShare,
  resolveErrorMessage,
  resolvePlotHeight,
  useElementWidth,
  useIsDarkTheme,
  usePrefersReducedMotion,
} from "../chartUtils";
// The elbow leader label and its collision handling are shared with the GRC dashboard donut and the
// AI companion's chat donut, so all three space a crowded tail the same way.
import {
  fitLeaderLabelPlot,
  leaderPlotIdealWidth,
  makeLeaderLabelRenderer,
} from "../../charts/donutCore";

/**
 * Recharts' own `<Pie>` defaults, which this chart does not override. The leader-label resolver has
 * to work from the same sweep recharts draws, so they are named here rather than repeated as bare
 * numbers at the call site.
 */
const RECHARTS_DEFAULT_START_ANGLE = 0;
const RECHARTS_DEFAULT_END_ANGLE = 360;

/** Named because the label fitting solves against it. */
const OUTER_RADIUS_RATIO = 0.86;

/** Leader labels raise this — see `maxPlotWidth` below. */
const PLOT_MAX_WIDTH = 280;

const SIDE_BY_SIDE_PLOT_SHARE = 0.48;
import {
  ChartEmptyState,
  ChartErrorBoundary,
  ChartErrorState,
  ChartLoadingState,
  ChartWarnings,
} from "../ChartStates";
import { SliceLegend, SliceTable, SliceTooltip } from "./DonutChrome";
import { normalizeChartData, type ChartSlice, type SliceSort } from "./donutUtils";

export interface DonutPieChartProps {
  /** Stays on the same DOM element across loading/error/empty/plotted states; also seeds internal title/plot node ids. */
  readonly id?: string;
  /** Rendered as `data-name` (not a valid div attribute); passed back as the third argument to onSliceClick. */
  readonly name?: string;

  /** Null, undefined, and non-arrays resolve to the empty state. */
  readonly data: unknown;
  readonly variant?: "donut" | "pie";
  /** Ignored by pie. */
  readonly innerRadiusRatio?: number;

  readonly nameKey?: string;
  readonly valueKey?: string;
  readonly colorKey?: string;

  readonly title?: string;
  readonly subtitle?: string;

  /** The chart stays square and centres inside it. */
  readonly height?: number;
  /** Assigned in order, never cycled. */
  readonly palette?: readonly string[];

  readonly loading?: boolean;
  /** Takes precedence over every other state. */
  readonly error?: string | Error | null;
  readonly onRetry?: () => void;
  readonly emptyMessage?: string;
  /** Shown instead of emptyMessage when every category is present but zero-valued. */
  readonly zeroValuesMessage?: string;

  /** Slices past this cap fold into a single neutral bucket. */
  readonly maxSlices?: number;
  readonly otherLabel?: string;
  readonly sort?: SliceSort;

  readonly showLegend?: boolean;
  readonly legendPosition?: "top" | "right" | "bottom";
  readonly legendToggle?: boolean;
  /** Both default off: values are already shown on slices/tooltip, and repeating them in the legend cramped rows on narrow cards. */
  readonly showLegendValue?: boolean;
  readonly showLegendShare?: boolean;

  /** Defaults on for pie, off for donut. */
  readonly showSliceLabels?: boolean;
  /** Ignored by `"leader"`. */
  readonly minLabelShare?: number;
  /**
   * How a slice's percentage is drawn.
   *
   * `"inside"` (default) writes it on the slice and drops anything under {@link minLabelShare},
   * because there is no room inside a sliver — small categories simply go unlabelled.
   *
   * `"leader"` runs an elbow line out to the label instead, so every slice keeps its number however
   * thin it is. Labels that would land on top of each other are pushed apart by the shared
   * resolver, which is what makes a long tail of sub-1% slices readable rather than a smear. Costs
   * room outside the arc — the plot reserves a margin for it — so it wants a chart with a bit of
   * space around it.
   */
  readonly labelStyle?: "inside" | "leader";

  readonly showTooltip?: boolean;
  readonly showDataTable?: boolean;
  readonly showWarnings?: boolean;

  /** Ignored by the pie variant. */
  readonly showCenterTotal?: boolean;
  readonly centerLabel?: string;
  readonly centerValue?: ReactNode;

  readonly unit?: string;
  readonly valueFormatter?: (value: number) => string;

  readonly onSliceClick?: (slice: ChartSlice, index: number, name?: string) => void;

  readonly className?: string;
  readonly ariaLabel?: string;
}


/** Pie's active-shape props aren't part of recharts' public type exports, so this narrows just the fields used here. */
interface ActiveSectorProps {
  cx?: number;
  cy?: number;
  innerRadius?: number;
  outerRadius?: number;
  startAngle?: number;
  endAngle?: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  cornerRadius?: number;
}

function renderActiveShape(props: unknown): JSX.Element {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, stroke, strokeWidth, cornerRadius } =
    props as ActiveSectorProps;
  if (!Number.isFinite(outerRadius)) return <g />;
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      // Same radius as resting shape: growing it used to push thin slices' rounded caps over their neighbour on hover.
      outerRadius={outerRadius}
      startAngle={startAngle}
      endAngle={endAngle}
      cornerRadius={cornerRadius}
      fill={fill}
      // Fill is transparent (outline-only slice); stroke must carry through or the hovered arc becomes invisible.
      stroke={stroke}
      strokeWidth={Number.isFinite(strokeWidth) ? Number(strokeWidth) + 1 : strokeWidth}
      style={stroke ? { filter: markGlow(stroke) } : undefined}
    />
  );
}

interface SliceLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  minShare: number;
}

/** White on the fill is the one place text may sit on a data colour; a shadow keeps it legible on light hues. */
function renderSliceLabel(props: SliceLabelProps) {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent, minShare } = props ?? {};
  if (!Number.isFinite(percent) || percent < minShare) return null;
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(midAngle)) return null;

  const RAD = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.62;
  const x = cx + radius * Math.cos(-midAngle * RAD);
  const y = cy + radius * Math.sin(-midAngle * RAD);

  return (
    <text
      x={x}
      y={y}
      fill="#ffffff"
      fontSize={11}
      fontWeight={700}
      textAnchor="middle"
      dominantBaseline="central"
      style={{ textShadow: "0 1px 2px rgba(0,0,0,0.45)", pointerEvents: "none" }}
    >
      {Math.round(percent * 100)}%
    </text>
  );
}

interface DonutPieChartHeaderProps {
  readonly id?: string;
  readonly title?: string;
  readonly subtitle?: string;
}

function DonutPieChartHeader({ id, title, subtitle }: Readonly<DonutPieChartHeaderProps>) {
  if (!title && !subtitle) return null;
  return (
    <div className="mb-3 min-w-0">
      {title && (
        <h3
          id={id ? `${id}-title` : undefined}
          className="break-words text-sm font-bold leading-snug text-foreground"
        >
          {title}
        </h3>
      )}
      {subtitle && (
        <p className="mt-0.5 break-words text-xs leading-snug text-muted-foreground">{subtitle}</p>
      )}
    </div>
  );
}

interface DonutCenterOverlayProps {
  readonly isDonut: boolean;
  readonly showCenterTotal: boolean;
  readonly centerValue?: ReactNode;
  readonly activeSlice: ChartSlice | null;
  readonly visibleTotal: number;
  readonly unit: string;
  readonly centerLabel: string;
}

function DonutCenterOverlay({
  isDonut,
  showCenterTotal,
  centerValue,
  activeSlice,
  visibleTotal,
  unit,
  centerLabel,
}: Readonly<DonutCenterOverlayProps>) {
  if (!isDonut || !showCenterTotal) return null;

  const displayTotal = activeSlice
    ? `${formatCompact(activeSlice.value)}${unit}`
    : `${formatCompact(visibleTotal)}${unit}`;
  const displayLabel = activeSlice
    ? `${formatShare(activeSlice.value, visibleTotal)} of total`
    : centerLabel;

  return (
    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
      <span className="break-words text-lg font-bold leading-none text-foreground">
        {centerValue ?? displayTotal}
      </span>
      <span className="mt-1 break-words text-[10px] font-medium leading-tight text-muted-foreground">
        {displayLabel}
      </span>
    </div>
  );
}

function resolveNoDataMessage(
  slicesCount: number,
  visibleTotal: number,
  zeroValuesMessage: string,
  emptyMessage: string,
): string {
  const fallback = "No data is available";
  if (slicesCount > 0 && visibleTotal <= 0) {
    if (zeroValuesMessage === "Every category in this set has a value of zero.") {
      return fallback;
    }
    return zeroValuesMessage || fallback;
  }
  if (emptyMessage === "There are no records to chart for the current selection.") {
    return fallback;
  }
  return emptyMessage || fallback;
}

interface PlotGeometryOptions {
  readonly labelsOn: boolean;
  readonly labelStyle?: "inside" | "leader";
  readonly plottableSlices: readonly ChartSlice[];
  readonly plotHeight: number;
  readonly containerWidth: number;
  readonly sideBySide: boolean;
}

function resolvePlotGeometry({
  labelsOn,
  labelStyle,
  plottableSlices,
  plotHeight,
  containerWidth,
  sideBySide,
}: PlotGeometryOptions) {
  const paddingAngle = plottableSlices.length > 1 ? SEGMENT_PADDING_ANGLE : 0;
  const leaderLabels = labelsOn && labelStyle === "leader";
  const sliceLabel = leaderLabels
    ? makeLeaderLabelRenderer(
        plottableSlices.map((slice) => slice.value),
        RECHARTS_DEFAULT_START_ANGLE,
        RECHARTS_DEFAULT_END_ANGLE,
        paddingAngle,
      )
    : undefined;
  const maxPlotWidth = leaderLabels
    ? Math.max(PLOT_MAX_WIDTH, leaderPlotIdealWidth(plotHeight))
    : PLOT_MAX_WIDTH;
  const boxWidth = sideBySide ? containerWidth * SIDE_BY_SIDE_PLOT_SHARE : containerWidth;
  const plotWidth = Math.min(boxWidth || plotHeight, maxPlotWidth);
  const plotFit = leaderLabels
    ? fitLeaderLabelPlot(plotWidth, plotHeight, OUTER_RADIUS_RATIO)
    : null;
  const plotMargin = plotFit
    ? {
        top: plotFit.marginY,
        right: plotFit.marginX,
        bottom: plotFit.marginY,
        left: plotFit.marginX,
      }
    : undefined;

  return { paddingAngle, sliceLabel, maxPlotWidth, plotMargin };
}

interface ChartDescriptionOptions {
  readonly ariaLabel?: string;
  readonly isDonut: boolean;
  readonly title?: string;
  readonly plottableSlices: readonly ChartSlice[];
  readonly visibleTotal: number;
  readonly unit: string;
  readonly formatValue: (value: number) => string;
}

function buildChartDescription({
  ariaLabel,
  isDonut,
  title,
  plottableSlices,
  visibleTotal,
  unit,
  formatValue,
}: ChartDescriptionOptions): string {
  if (ariaLabel) return ariaLabel;
  const kind = isDonut ? "Donut" : "Pie";
  const base = title ? `${kind} chart: ${title}.` : `${kind} chart.`;
  if (!plottableSlices.length) return `${base} No data.`;
  const parts = plottableSlices
    .map((slice) => `${slice.label} ${formatValue(slice.value)}${unit}, ${formatShare(slice.value, visibleTotal)}`)
    .join(". ");
  return `${base} Total ${formatValue(visibleTotal)}${unit}. ${parts}.`;
}

function getFlexLayoutClasses(sideBySide: boolean, legendOnTop: boolean): string {
  return cn(
    "flex min-w-0 items-center gap-3",
    sideBySide ? "flex-row" : "flex-col",
    legendOnTop && "flex-col-reverse",
  );
}

interface DonutPlotSurfaceProps {
  readonly id?: string;
  readonly sideBySide: boolean;
  readonly plotHeight: number;
  readonly maxPlotWidth: number;
  readonly plotMargin?: {
    readonly top: number;
    readonly right: number;
    readonly bottom: number;
    readonly left: number;
  };
  readonly description: string;
  readonly plottableSlices: readonly ChartSlice[];
  readonly isDonut: boolean;
  readonly innerRadiusRatio: number;
  readonly paddingAngle: number;
  readonly sliceLabel?: unknown;
  readonly minLabelShare: number;
  readonly labelsOn: boolean;
  readonly activeIndex: number;
  readonly activeId: string | null;
  readonly pointerInside: boolean;
  readonly reduceMotion: boolean;
  readonly visibleTotal: number;
  readonly unit: string;
  readonly formatValue: (value: number) => string;
  readonly showTooltip: boolean;
  readonly showCenterTotal: boolean;
  readonly centerLabel: string;
  readonly centerValue?: ReactNode;
  readonly activeSlice: ChartSlice | null;
  readonly onSliceClick?: (slice: ChartSlice, index: number, name?: string) => void;
  readonly onSliceSelect: (id: string | null) => void;
  readonly onPointerInside: (inside: boolean) => void;
  readonly onSliceClickCallback: (_: unknown, index: number) => void;
}

function DonutPlotSurface({
  id,
  sideBySide,
  plotHeight,
  maxPlotWidth,
  plotMargin,
  description,
  plottableSlices,
  isDonut,
  innerRadiusRatio,
  paddingAngle,
  sliceLabel,
  minLabelShare,
  labelsOn,
  activeIndex,
  activeId,
  pointerInside,
  reduceMotion,
  visibleTotal,
  unit,
  formatValue,
  showTooltip,
  showCenterTotal,
  centerLabel,
  centerValue,
  activeSlice,
  onSliceClick,
  onSliceSelect,
  onPointerInside,
  onSliceClickCallback,
}: Readonly<DonutPlotSurfaceProps>) {
  const handleLeave = () => {
    onSliceSelect(null);
    onPointerInside(false);
  };

  const pieLabel = labelsOn
    ? (sliceLabel ??
      ((props: unknown) =>
        renderSliceLabel({ ...(props as SliceLabelProps), minShare: minLabelShare })))
    : undefined;

  return (
    <div
      className={cn("relative w-full shrink-0", sideBySide ? "w-[48%]" : "mx-auto")}
      id={id ? `${id}-plot` : undefined}
      style={{ height: plotHeight, maxWidth: maxPlotWidth }}
      onMouseLeave={handleLeave}
      role="img"
      aria-label={description}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart onMouseLeave={handleLeave} margin={plotMargin}>
          {showTooltip && (
            <Tooltip
              content={
                <SliceTooltip
                  total={visibleTotal}
                  unit={unit}
                  valueFormatter={formatValue}
                />
              }
              active={pointerInside && activeId != null}
              wrapperStyle={{ outline: "none", zIndex: 20 }}
              isAnimationActive={!reduceMotion}
            />
          )}
          <Pie
            data={plottableSlices as ChartSlice[]}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius={isDonut ? `${innerRadiusRatio * 100}%` : 0}
            outerRadius={`${OUTER_RADIUS_RATIO * 100}%`}
            paddingAngle={paddingAngle}
            cornerRadius={isDonut ? 2 : 0}
            minAngle={2}
            label={pieLabel}
            labelLine={false}
            activeIndex={activeIndex >= 0 ? activeIndex : undefined}
            activeShape={renderActiveShape}
            onMouseEnter={(_, index: number) => {
              onSliceSelect(plottableSlices?.[index]?.id ?? null);
              onPointerInside(true);
            }}
            onClick={onSliceClickCallback}
            isAnimationActive={!reduceMotion}
            animationDuration={600}
            className={onSliceClick ? "cursor-pointer" : undefined}
          >
            {plottableSlices.map((slice) => {
              const isSelected = activeId == null || activeId === slice.id;
              return (
                <Cell
                  key={slice.id}
                  fill={BAR_FILL}
                  stroke={slice.color}
                  strokeWidth={MARK_STROKE_WIDTH}
                  style={{
                    transition: "opacity 200ms ease",
                    opacity: isSelected ? 1 : 0.4,
                    outline: "none",
                    filter: markGlow(slice.color),
                  }}
                />
              );
            })}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      <DonutCenterOverlay
        isDonut={isDonut}
        showCenterTotal={showCenterTotal}
        centerValue={centerValue}
        activeSlice={activeSlice}
        visibleTotal={visibleTotal}
        unit={unit}
        centerLabel={centerLabel}
      />
    </div>
  );
}

function DonutPieChartInner({
  id,
  name,
  data,
  variant = "donut",
  innerRadiusRatio = 0.6,
  nameKey = "name",
  valueKey = "value",
  colorKey = "color",
  title,
  subtitle,
  height = 260,
  palette,
  loading = false,
  error = null,
  onRetry,
  emptyMessage = "There are no records to chart for the current selection.",
  zeroValuesMessage = "Every category in this set has a value of zero.",
  maxSlices = 6,
  otherLabel = "Other",
  sort = "desc",
  showLegend = true,
  legendPosition = "top",
  legendToggle = true,
  showLegendValue = false,
  showLegendShare = false,
  showSliceLabels,
  minLabelShare = 0.08,
  labelStyle = "inside",
  showTooltip = true,
  showDataTable = false,
  showWarnings = true,
  showCenterTotal = true,
  centerLabel = "Total",
  centerValue,
  unit = "",
  valueFormatter,
  onSliceClick,
  className,
  ariaLabel,
}: Readonly<DonutPieChartProps>) {
  const isDark = useIsDarkTheme();
  const reduceMotion = usePrefersReducedMotion();
  const [containerRef, containerWidth] = useElementWidth<HTMLDivElement>();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [pointerInside, setPointerInside] = useState(false);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());
  const [tableOpen, setTableOpen] = useState(false);

  const isDonut = variant === "donut";
  const plotHeight = resolvePlotHeight(height, 260);
  const activePalette = useMemo(() => {
    const source = palette?.filter?.(Boolean);
    if (source?.length) return source;
    return isDark ? CHART_PALETTE_DARK : CHART_PALETTE_LIGHT;
  }, [palette, isDark]);

  const formatValue = useCallback(
    (value: number) => {
      if (typeof valueFormatter === "function") {
        try {
          return valueFormatter(value);
        } catch {
          return formatNumber(value);
        }
      }
      return formatNumber(value);
    },
    [valueFormatter],
  );

  const { slices, warnings } = useMemo(
    () =>
      normalizeChartData(data, {
        nameKey,
        valueKey,
        colorKey,
        palette: activePalette,
        otherColor: isDark ? OTHER_COLOR_DARK : OTHER_COLOR_LIGHT,
        maxSlices,
        otherLabel,
        sort,
      }),
    [data, nameKey, valueKey, colorKey, activePalette, isDark, maxSlices, otherLabel, sort],
  );

  const visibleSlices = useMemo(
    () => slices.filter((slice) => !hiddenIds.has(slice.id)),
    [slices, hiddenIds],
  );
  const visibleTotal = useMemo(
    () => visibleSlices.reduce((sum, slice) => sum + slice.value, 0),
    [visibleSlices],
  );

  const plottableSlices = useMemo(
    () => visibleSlices.filter((slice) => slice.value > 0),
    [visibleSlices],
  );

  const activeIndex = useMemo(
    () => plottableSlices.findIndex((slice) => slice.id === activeId),
    [plottableSlices, activeId],
  );
  const activeSlice = activeIndex >= 0 ? plottableSlices[activeIndex] : null;

  const handleToggle = useCallback(
    (id: string) => {
      if (!legendToggle) return;
      setHiddenIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          return next;
        }
        next.add(id);
        return next;
      });
    },
    [legendToggle],
  );

  const handleSliceClick = useCallback(
    (_: unknown, index: number) => {
      const slice = plottableSlices?.[index];
      if (slice && typeof onSliceClick === "function") onSliceClick(slice, index, name);
    },
    [plottableSlices, onSliceClick, name],
  );

  const description = useMemo(
    () =>
      buildChartDescription({
        ariaLabel,
        isDonut,
        title,
        plottableSlices,
        visibleTotal,
        unit,
        formatValue,
      }),
    [ariaLabel, isDonut, title, plottableSlices, visibleTotal, unit, formatValue],
  );

  const errorMessage = resolveErrorMessage(error);
  const labelsOn = showSliceLabels ?? !isDonut;
  const sideBySide = legendPosition === "right" && containerWidth >= 400;
  const legendOnTop = legendPosition === "top";

  const { paddingAngle, sliceLabel, maxPlotWidth, plotMargin } = useMemo(
    () =>
      resolvePlotGeometry({
        labelsOn,
        labelStyle,
        plottableSlices,
        plotHeight,
        containerWidth,
        sideBySide,
      }),
    [labelsOn, labelStyle, plottableSlices, plotHeight, containerWidth, sideBySide],
  );

  const header = <DonutPieChartHeader id={id} title={title} subtitle={subtitle} />;

  if (errorMessage) {
    return (
      <div
        id={id}
        data-name={name}
        ref={containerRef}
        className={cn("w-full min-w-0", className)}
      >
        {header}
        <ChartErrorState height={plotHeight} message={errorMessage} onRetry={onRetry} />
      </div>
    );
  }

  if (loading) {
    return (
      <div
        id={id}
        data-name={name}
        ref={containerRef}
        className={cn("w-full min-w-0", className)}
      >
        {header}
        <ChartLoadingState height={plotHeight} />
      </div>
    );
  }

  const legendElement = showLegend && slices.length > 0 && (
    <SliceLegend
      slices={slices}
      total={visibleTotal}
      unit={unit}
      activeId={activeId}
      hiddenIds={hiddenIds}
      position={sideBySide ? "right" : "bottom"}
      interactive={legendToggle}
      showValue={showLegendValue}
      showShare={showLegendShare}
      valueFormatter={formatValue}
      onActivate={setActiveId}
      onToggle={handleToggle}
    />
  );

  if (!plottableSlices.length || visibleTotal <= 0) {
    const noDataMessage = resolveNoDataMessage(
      slices.length,
      visibleTotal,
      zeroValuesMessage,
      emptyMessage,
    );

    return (
      <div
        id={id}
        data-name={name}
        ref={containerRef}
        className={cn("w-full min-w-0", className)}
      >
        {header}
        <div className={getFlexLayoutClasses(sideBySide, legendOnTop)}>
          <div className="w-full">
            <ChartEmptyState
              height={plotHeight}
              message={noDataMessage}
            />
          </div>
          {legendElement}
        </div>
        {showWarnings && <ChartWarnings warnings={warnings} />}
      </div>
    );
  }

  return (
    <div
      id={id}
      data-name={name}
      ref={containerRef}
      className={cn("w-full min-w-0", className)}
    >
      {header}

      <div className={getFlexLayoutClasses(sideBySide, legendOnTop)}>
        <DonutPlotSurface
          id={id}
          sideBySide={sideBySide}
          plotHeight={plotHeight}
          maxPlotWidth={maxPlotWidth}
          plotMargin={plotMargin}
          description={description}
          plottableSlices={plottableSlices}
          isDonut={isDonut}
          innerRadiusRatio={innerRadiusRatio}
          paddingAngle={paddingAngle}
          sliceLabel={sliceLabel}
          minLabelShare={minLabelShare}
          labelsOn={labelsOn}
          activeIndex={activeIndex}
          activeId={activeId}
          pointerInside={pointerInside}
          reduceMotion={reduceMotion}
          visibleTotal={visibleTotal}
          unit={unit}
          formatValue={formatValue}
          showTooltip={showTooltip}
          showCenterTotal={showCenterTotal}
          centerLabel={centerLabel}
          centerValue={centerValue}
          activeSlice={activeSlice}
          onSliceClick={onSliceClick}
          onSliceSelect={setActiveId}
          onPointerInside={setPointerInside}
          onSliceClickCallback={handleSliceClick}
        />

        {legendElement}
      </div>

      {showWarnings && <ChartWarnings warnings={warnings} />}

      {showDataTable && (
        <SliceTable
          slices={visibleSlices}
          total={visibleTotal}
          unit={unit}
          categoryHeader={title || "Category"}
          open={tableOpen}
          valueFormatter={formatValue}
          onToggle={() => setTableOpen((open) => !open)}
        />
      )}
    </div>
  );
}

/**
 * Public entry point. Wraps the chart in a local error boundary so a render
 * crash stays inside the chart box instead of reaching the app-level boundary
 * and blanking the page.
 */
export function DonutPieChart(props: Readonly<DonutPieChartProps>) {
  const plotHeight = resolvePlotHeight(props?.height, 260);

  return (
    <ChartErrorBoundary height={plotHeight} onRetry={props?.onRetry}>
      <DonutPieChartInner {...props} />
    </ChartErrorBoundary>
  );
}

export default DonutPieChart;
